import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

const AccompanimentFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      id: 1,
      question: 'Combien de temps pour livrer un premier projet IA ?',
      answer: 'À partir de 30 jours pour un projet ciblé. Le cadrage prend 1 à 2 semaines, la livraison 4 à 8 semaines pour les projets plus ambitieux, et l\'ancrage démarre dès la mise en production.'
    },
    {
      id: 2,
      question: 'Mon équipe doit-elle être formée avant le projet ?',
      answer: 'Non. La formation des équipes est intégrée au parcours de livraison, pour qu\'elle soit immédiatement applicable au projet en cours.'
    },
    {
      id: 3,
      question: 'Quels types de projets pouvez-vous livrer ?',
      answer: 'Agents IA conversationnels, automatisations de processus (no-code / low-code), assistants spécialisés métier, outils d\'analyse documentaire, génération de devis ou rapports, mise à jour automatisée de CRM, etc.'
    },
    {
      id: 4,
      question: 'Est-ce finançable par mon OPCO ?',
      answer: 'La partie formation est éligible au plan de développement des compétences. Nous vous aidons à structurer votre demande de prise en charge.'
    },
    {
      id: 5,
      question: 'Êtes-vous conformes RGPD et EU AI Act ?',
      answer: 'Oui. Nos projets intègrent dès la conception les exigences RGPD et EU AI Act, avec documentation des traitements, choix d\'hébergement européen et politique d\'usage adaptée.'
    }
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <section id="faq" className="section-padding bg-gray-50">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="heading-lg mb-4">Questions fréquentes</h2>
          <p className="text-lg text-gray-600">Trouvez les réponses aux questions les plus courantes</p>
        </div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, idx) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.05 }}
              viewport={{ once: true }}
              className="mb-4"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full bg-white rounded-lg p-6 text-left hover:shadow-md transition-shadow flex items-start justify-between gap-4"
                aria-expanded={openIndex === idx}
              >
                <span className="font-semibold text-gray-900">{faq.question}</span>
                <ChevronDown
                  className={`h-5 w-5 text-primary flex-shrink-0 transition-transform duration-300 ${
                    openIndex === idx ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>

              {openIndex === idx && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white border-t border-gray-200 px-6 py-4 text-gray-600"
                >
                  {faq.answer}
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </div>
    </section>
  );
};

export default AccompanimentFAQ;
